import React, { useState, useEffect } from 'react';
import { supabase } from '../../../config/supabase';
import './CategoriesModal.css';

const CategoriesModal = ({ categories, onClose, onCategoryChange }) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('📁');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoriesList, setCategoriesList] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    setLoading(true);
    setError('');

    try {
      // Check if category already exists
      const { data: existing } = await supabase
        .from('categories')
        .select('id')
        .eq('name', newCategoryName.trim())
        .single();

      if (existing) {
        setError('Category already exists');
        setLoading(false);
        return;
      }

      // Insert new category into Supabase
      const { data, error: insertError } = await supabase
        .from('categories')
        .insert([
          {
            name: newCategoryName.trim(),
            icon: newCategoryIcon || '📁',
            display_order: categoriesList.length,
            is_active: true
          }
        ])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Refresh categories list
      await fetchCategories();
      
      setNewCategoryName('');
      setNewCategoryIcon('📁');
    } catch (err) {
      console.error('Error adding category:', err);
      setError(err.message || 'Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Delete from Supabase
      const { error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (deleteError) {
        throw deleteError;
      }

      // Refresh categories list
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message || 'Failed to delete category');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      setCategoriesList(data || []);
      
      // Update parent component with new categories
      const categoryNames = data.map(cat => cat.name);
      onCategoryChange(['All', ...categoryNames]);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategoriesList([]);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="categories-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Manage Categories</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content">
          <div className="categories-list">
            <h3 className="section-title">Existing Categories</h3>
            {error && <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
            {categoriesList.length > 0 ? (
              <div className="category-items">
                {categoriesList.map((category) => (
                  <div key={category.id} className="category-item">
                    <div className="category-info">
                      <span className="category-icon">{category.icon || '📁'}</span>
                      <span className="category-name">{category.name}</span>
                    </div>
                    <div className="category-actions">
                      <button className="edit-category-btn" title="Edit">
                        ✏️
                      </button>
                      <button
                        className="delete-category-btn"
                        onClick={() => handleDeleteCategory(category.id, category.name)}
                        title="Delete"
                        disabled={loading}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-categories">No categories yet</p>
            )}
          </div>

          <div className="add-category-section">
            <h3 className="section-title">Add New Category</h3>
            <div className="add-category-form">
              <div className="form-group">
                <label className="form-label">Category Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Icon (Emoji)</label>
                <input
                  type="text"
                  className="form-input icon-input"
                  placeholder="📁"
                  value={newCategoryIcon}
                  onChange={(e) => setNewCategoryIcon(e.target.value)}
                  maxLength="2"
                />
              </div>
              {error && <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
              <button
                className="add-category-btn"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim() || loading}
              >
                {loading ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoriesModal;

